import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Container,
  Paper,
  Button,
  Avatar,
  Chip,
  Fade,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  Business, 
  Assistant, 
  ArrowForward, 
  Logout,
  Dashboard,
  CheckCircle
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { organizationService, authService } from '../utils/api';

function OrganizationSelector() {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrgId, setSelectedOrgId] = useState(null);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const response = await organizationService.getAllOrganizations();
      setOrganizations(response.organizations);
    } catch (error) {
      toast.error(error.message || 'Failed to fetch organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleOrganizationSelect = (organization) => {
    setSelectedOrgId(organization.id);
    setTimeout(() => {
      organizationService.setSelectedOrganization(organization);
      navigate('/dashboard');
    }, 300);
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#ffffff',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Animated gradient background */}
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            background: `linear-gradient(
              135deg,
              rgba(59, 130, 246, 0.2) 0%,
              rgba(147, 197, 253, 0.3) 25%,
              rgba(255, 255, 255, 0.1) 50%,
              rgba(59, 130, 246, 0.4) 75%,
              rgba(37, 99, 235, 0.5) 100%
            )`,
            backgroundSize: '400% 400%',
            animation: 'wave 8s ease-in-out infinite',
            backgroundImage: `
              radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.3) 0%, transparent 50%),
              radial-gradient(circle at 80% 50%, rgba(147, 197, 253, 0.4) 0%, transparent 50%),
              radial-gradient(circle at 40% 60%, rgba(255, 255, 255, 0.2) 0%, transparent 30%),
              radial-gradient(circle at 60% 40%, rgba(59, 130, 246, 0.2) 0%, transparent 40%),
              radial-gradient(circle at 30% 70%, rgba(37, 99, 235, 0.3) 0%, transparent 35%),
              radial-gradient(circle at 70% 30%, rgba(147, 197, 253, 0.25) 0%, transparent 45%)
            `,
            '@keyframes wave': {
              '0%, 100%': {
                backgroundPosition: '0% 50%'
              },
              '50%': {
                backgroundPosition: '100% 50%'
              }
            }
          }}
        />
        
        <Box sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" sx={{ mt: 3, color: '#1a1a1a' }}>
            Loading organizations...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#ffffff',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Animated gradient background */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1,
          background: `linear-gradient(
            135deg,
            rgba(59, 130, 246, 0.2) 0%,
            rgba(147, 197, 253, 0.3) 25%,
            rgba(255, 255, 255, 0.1) 50%,
            rgba(59, 130, 246, 0.4) 75%,
            rgba(37, 99, 235, 0.5) 100%
          )`,
          backgroundSize: '400% 400%',
          animation: 'wave 8s ease-in-out infinite',
          backgroundImage: `
            radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.3) 0%, transparent 50%),
            radial-gradient(circle at 80% 50%, rgba(147, 197, 253, 0.4) 0%, transparent 50%),
            radial-gradient(circle at 40% 60%, rgba(255, 255, 255, 0.2) 0%, transparent 30%),
            radial-gradient(circle at 60% 40%, rgba(59, 130, 246, 0.2) 0%, transparent 40%),
            radial-gradient(circle at 30% 70%, rgba(37, 99, 235, 0.3) 0%, transparent 35%),
            radial-gradient(circle at 70% 30%, rgba(147, 197, 253, 0.25) 0%, transparent 45%)
          `,
          '@keyframes wave': {
            '0%, 100%': {
              backgroundPosition: '0% 50%'
            },
            '50%': {
              backgroundPosition: '100% 50%'
            }
          }
        }}
      />

      {/* Header with logout */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          p: 3,
          zIndex: 10
        }}
      >
        <Tooltip title="Logout">
          <IconButton
            onClick={handleLogout}
            sx={{
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              color: '#3b82f6',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(59, 130, 246, 0.2)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(59, 130, 246, 0.25)'
              }
            }}
          >
            <Logout />
          </IconButton>
        </Tooltip>
      </Box>

      <Container maxWidth="lg" sx={{ py: 6, position: 'relative', zIndex: 2 }}>
        {/* Header Section */}
        <Fade in timeout={600}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                margin: '0 auto',
                mb: 3,
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                border: '2px solid rgba(59, 130, 246, 0.2)'
              }}
            >
              <Dashboard sx={{ fontSize: 40, color: '#3b82f6' }} />
            </Avatar>
            
            <Typography 
              variant="h3" 
              sx={{ 
                mb: 2, 
                fontWeight: 700,
                color: '#1a1a1a',
                letterSpacing: '-0.02em'
              }}
            >
              Choose Your Workspace
            </Typography>
            
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#6b7280',
                fontWeight: 400,
                lineHeight: 1.6,
                maxWidth: 600,
                margin: '0 auto'
              }}
            >
              Select a workspace to access your dashboard and manage your calls
            </Typography>
          </Box>
        </Fade>

        {/* Organizations Grid */}
        <Grid container spacing={4} justifyContent="center">
          {organizations.map((organization, index) => (
            <Grid item xs={12} sm={6} md={4} key={organization.id}>
              <Fade in timeout={800 + index * 200}>
                <Card
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    background: selectedOrgId === organization.id 
                      ? '#3b82f6'
                      : 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(20px)',
                    border: selectedOrgId === organization.id 
                      ? '2px solid #3b82f6'
                      : '1px solid rgba(229, 231, 235, 0.5)',
                    borderRadius: 2,
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: selectedOrgId === organization.id 
                      ? '0 8px 25px rgba(59, 130, 246, 0.25)'
                      : '0 4px 15px rgba(0, 0, 0, 0.1)',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: selectedOrgId === organization.id 
                        ? '0 12px 35px rgba(59, 130, 246, 0.3)'
                        : '0 8px 25px rgba(0, 0, 0, 0.15)',
                      '& .org-arrow': {
                        transform: 'translateX(8px)',
                        opacity: 1
                      }
                    }
                  }}
                  onClick={() => handleOrganizationSelect(organization)}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center', position: 'relative' }}>
                    {/* Selection indicator */}
                    {selectedOrgId === organization.id && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 16,
                          right: 16
                        }}
                      >
                        <CheckCircle sx={{ color: 'white', fontSize: 28 }} />
                      </Box>
                    )}

                    {/* Organization Icon */}
                    <Avatar
                      sx={{
                        width: 70,
                        height: 70,
                        margin: '0 auto',
                        mb: 3,
                        backgroundColor: selectedOrgId === organization.id 
                          ? 'rgba(255, 255, 255, 0.2)' 
                          : '#3b82f6',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <Business sx={{ 
                        fontSize: 35, 
                        color: 'white'
                      }} />
                    </Avatar>

                    {/* Organization Name */}
                    <Typography
                      variant="h5"
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: selectedOrgId === organization.id ? 'white' : '#1a1a1a',
                        transition: 'color 0.3s ease'
                      }}
                    >
                      {organization.name}
                    </Typography>

                    {/* Assistant Info */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        mb: 3
                      }}
                    >
                      <Assistant
                        sx={{
                          fontSize: 18,
                          color: selectedOrgId === organization.id 
                            ? 'rgba(255, 255, 255, 0.8)' 
                            : '#6b7280'
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ 
                          fontWeight: 500,
                          color: selectedOrgId === organization.id 
                            ? 'rgba(255, 255, 255, 0.9)' 
                            : '#6b7280'
                        }}
                      >
                        {organization.assistant_name || 'Assistant'}
                      </Typography>
                    </Box>

                    {/* Status Chip */}
                    <Chip
                      label={organization.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{
                        mb: 3,
                        backgroundColor: selectedOrgId === organization.id 
                          ? 'rgba(255, 255, 255, 0.2)'
                          : organization.is_active ? '#10b981' : '#ef4444',
                        color: 'white',
                        fontWeight: 500,
                        border: selectedOrgId === organization.id 
                          ? '1px solid rgba(255, 255, 255, 0.3)'
                          : 'none'
                      }}
                    />

                    {/* Action Button */}
                    <Button
                      variant={selectedOrgId === organization.id ? "outlined" : "contained"}
                      fullWidth
                      endIcon={
                        <ArrowForward 
                          className="org-arrow"
                          sx={{ 
                            transition: 'all 0.3s ease',
                            opacity: 0.7
                          }} 
                        />
                      }
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: '1rem',
                        textTransform: 'none',
                        ...(selectedOrgId === organization.id ? {
                          borderColor: 'white',
                          color: 'white',
                          '&:hover': {
                            borderColor: 'white',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 15px rgba(255, 255, 255, 0.2)'
                          }
                        } : {
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: '#2563eb',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(59, 130, 246, 0.25)'
                          }
                        })
                      }}
                    >
                      {selectedOrgId === organization.id ? 'Selected' : 'Select Organization'}
                    </Button>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>

        {/* Empty State */}
        {organizations.length === 0 && (
          <Fade in timeout={1000}>
            <Paper
              elevation={0}
              sx={{
                p: 6,
                textAlign: 'center',
                mt: 4,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                borderRadius: 2,
                border: '1px solid rgba(229, 231, 235, 0.5)',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
              }}
            >
              <Business sx={{ fontSize: 64, color: '#6b7280', mb: 2 }} />
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: '#1a1a1a' }}>
                No Organizations Available
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: '#6b7280' }}>
                You don't have access to any organizations yet.
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                Please contact your administrator to get access to an organization.
              </Typography>
            </Paper>
          </Fade>
        )}
      </Container>
    </Box>
  );
}

export default OrganizationSelector; 